import React from 'react'
import { useGetPropertyDetailedDelayedFeesQuery } from 'redux/apis/interest-rates-api'
import { OrderTableItem } from '../models/order'
import { skipToken } from '@reduxjs/toolkit/dist/query'
import dayjs from 'dayjs'
import { Box, Divider, Grid, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatDate } from 'utils/date-utils'

type Props = {
    orderLine: OrderTableItem | undefined
    endDate: string | Date
    isSectionParentView: boolean
}

export const PropertyDelayedFeesDetailedView: React.FC<Props> = ({ orderLine, endDate, isSectionParentView }) => {
    const { t } = useTranslation()

    const { data, isSuccess } = useGetPropertyDetailedDelayedFeesQuery(
        !orderLine
            ? skipToken
            : {
                  orderId: orderLine.orderId,
                  orderLineId: orderLine.uniqueId,
                  endDate: dayjs(endDate).format('YYYY-MM-DD'),
                  targetMainProperty: isSectionParentView,
              },
    )

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {isSuccess && data?.delays.length === 0 && (
                <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
                    <Typography variant="h6">{t('TEXT_NO_DELAYED_FEES', 'No current delayed fees found!')}</Typography>
                </Box>
            )}
            {isSuccess &&
                data.delays.length !== 0 &&
                data.delays.map((delayed) => (
                    <Box key={delayed.fullInvoice.number} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div>
                            <Typography variant="body1">KID: {delayed.fullInvoice.kid}</Typography>
                            <Typography variant="body1">Invoice nr: {delayed.fullInvoice.number}</Typography>
                            <Typography variant="body1">
                                Issued: {delayed.fullInvoice.date ? formatDate(delayed.fullInvoice.date) : '-'}
                            </Typography>
                            <Typography variant="body1">
                                Due date: {delayed.fullInvoice.dueDate ? formatDate(delayed.fullInvoice.dueDate) : '-'}
                            </Typography>
                        </div>
                        <Box sx={{ border: '1px solid', p: 1 }}>
                            <Typography variant="body1" mb={2}>
                                Transactions
                            </Typography>

                            <Grid container rowSpacing={1}>
                                <Grid item xs={4}>
                                    Type
                                </Grid>
                                <Grid item xs={4}>
                                    Amount
                                </Grid>
                                <Grid item xs={4}>
                                    Date
                                </Grid>
                                {delayed.fullInvoice.transactions?.map((transaction, idx) => (
                                    <React.Fragment key={transaction.date?.toString() || idx}>
                                        <Grid item xs={4}>
                                            {transaction.typeName || transaction.type}
                                        </Grid>
                                        <Grid item xs={4}>
                                            {transaction.amount}
                                        </Grid>
                                        <Grid item xs={4}>
                                            {transaction.date ? formatDate(transaction.date) : '-'}
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>

                        <Box sx={{ border: '1px solid', p: 1 }}>
                            <Typography variant="body1" mb={2}>
                                Periods
                            </Typography>

                            <Grid container rowSpacing={1}>
                                <Grid item xs={2}>
                                    Start
                                </Grid>
                                <Grid item xs={2}>
                                    End
                                </Grid>
                                <Grid item xs={2}>
                                    Rate
                                </Grid>
                                <Grid item xs={2}>
                                    Interest
                                </Grid>
                                <Grid item xs={2}>
                                    End reason
                                </Grid>
                                <Grid item xs={2}>
                                    Start Principal
                                </Grid>

                                {delayed.periods.map((period) => (
                                    <React.Fragment key={`${period.periodStartDate} - ${period.periodEndDate}`}>
                                        <Grid item xs={2}>
                                            {formatDate(period.periodStartDate)}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {formatDate(period.periodEndDate)}
                                        </Grid>
                                        <Grid item xs={2}>
                                            {period.annualRateDuringPeriod * 100}
                                        </Grid>

                                        <Grid item xs={2}>
                                            {period.interestAccruedInPeriod}
                                        </Grid>

                                        <Grid item xs={2}>
                                            {period.periodChangeReason ?? '-'}
                                        </Grid>

                                        <Grid item xs={2}>
                                            {period.principalDuringPeriod}
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>

                        <Divider />
                    </Box>
                ))}
        </Box>
    )
}
